<template>
  <div class="pa-dev-card">
    <div class="pa-dev-head">
      <div class="pa-dev-eye">
        {{ t('property.access.devAccountManagerLocked.developerEyebrow', { developer: developerName }) }}
      </div>
      <div class="pa-dev-title">
        {{ t('property.access.devSalesManagerCard.salesContact', managers.length) }}
      </div>
    </div>

    <div class="pa-dev-rows">
      <div
        v-for="(m, i) in managers"
        :key="i"
        class="pa-dev-row"
      >
        <div
          class="pa-dev-av"
          :style="m.avatarUrl ? undefined : { background: avatarColor(m.name) }"
        >
          <img
            v-if="m.avatarUrl"
            :src="m.avatarUrl"
            :alt="m.name || t('property.access.devSalesManagerCard.defaultManagerAlt')"
            class="pa-dev-av-img"
          />
          <span v-else>{{ initials(m.name) }}</span>
        </div>
        <div class="pa-dev-mid">
          <div class="pa-dev-name">{{ m.name || t('property.access.devSalesManagerCard.defaultManager') }}</div>
          <div class="pa-dev-role">{{ m.role || t('property.access.devSalesManagerCard.defaultManager') }}</div>
          <a
            v-if="m.phone"
            :href="`tel:${m.phone}`"
            class="pa-dev-phone"
            dir="ltr"
            >{{ m.phone }}</a
          >
        </div>
        <a
          v-if="whatsappLink(m)"
          :href="whatsappLink(m)!"
          target="_blank"
          rel="noopener noreferrer"
          class="pa-dev-wa"
          :aria-label="t('property.accountManagerSidebar.messageOnWhatsapp')"
        >
          <UIIcon
            name="whatsapp"
            size="sm"
          />
          <span>{{ t('property.access.devSalesManagerCard.whatsapp') }}</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const { t } = useI18n();

export type DevSalesManager = {
  name?: string | null;
  role?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  avatarUrl?: string | null;
};

const { managers } = defineProps<{
  developerName: string;
  managers: DevSalesManager[];
}>();

// Sales-manager photos (photoUrl in the bot DB, now populated for most) render
// via the <img> when present; we fall back to a deterministic colored initials
// thumbnail for managers without one.
const initials = (name?: string | null): string => {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return 'SM';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

// Pleasant, deterministic background per name so multiple managers read as
// distinct avatars rather than identical placeholders.
const AVATAR_COLORS = ['#0d6e5f', '#1f5fc4', '#9c3b8e', '#b4612a', '#2f7d3a', '#6a4caa', '#a23a3a', '#3a6e9e'];
const avatarColor = (name?: string | null): string => {
  const key = (name || '').trim();
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

// wa.me requires digits only. Prefer an explicit whatsapp number, fall back to
// the listed phone. Returns null when neither yields enough digits to dial.
const whatsappLink = (m: DevSalesManager): string | null => {
  const digits = (m.whatsapp || m.phone || '').replace(/\D/g, '');
  return digits.length >= 8 ? `https://wa.me/${digits}` : null;
};
</script>

<style scoped lang="scss">
.pa-dev-card {
  background: #f8f7f3;
  border-radius: 16px;
  padding: 18px;
}

.pa-dev-head {
  margin-bottom: 14px;
}

.pa-dev-eye {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(13, 15, 21, 0.62);
  margin-bottom: 6px;
}

.pa-dev-title {
  font-family: 'Fraunces', 'Times New Roman', serif;
  font-weight: 600;
  font-size: 16px;
  letter-spacing: -0.01em;
  color: #0d0f15;
}

.pa-dev-rows > * + * {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(13, 15, 21, 0.08);
}

.pa-dev-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pa-dev-av {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
  display: grid;
  place-items: center;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.35) inset;
}

.pa-dev-av-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pa-dev-mid {
  flex-grow: 1;
  min-width: 0;
}

.pa-dev-name {
  font-size: 13px;
  font-weight: 700;
  color: #0d0f15;
}

.pa-dev-role {
  font-size: 11px;
  font-weight: 500;
  color: rgba(13, 15, 21, 0.62);
  margin-top: 1px;
}

.pa-dev-phone {
  display: inline-block;
  margin-top: 3px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
  font-weight: 600;
  color: #0d0f15;

  &:hover {
    text-decoration: underline;
  }
}

.pa-dev-wa {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  height: 34px;
  padding: 0 12px;
  border-radius: 9px;
  background: #25d366;
  color: white;
  font-size: 12px;
  font-weight: 700;
  transition: background 0.15s ease;

  &:hover {
    background: #1fb655;
  }
}
</style>
